import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, query, orderBy, where } from "firebase/firestore";
import { db } from "../firebase";
import './AdminDashboard.css';

const AdminTournamentParticipationTracker = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('participationCount'); // 'participationCount', 'pseudo', 'lastParticipation'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [filterMin, setFilterMin] = useState(0);
  const [filterMax, setFilterMax] = useState(999);

  // Load all users and their tournament participation counts
  useEffect(() => {
    const loadUserParticipationData = async () => {
      try {
        setLoading(true);
        
        // Get all users
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersData = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // For each user, count their tournament participations
        const usersWithParticipation = await Promise.all(
          usersData.map(async (user) => {
            try {
              // Get user's tournament history
              const tournamentsSnapshot = await getDocs(
                collection(db, 'users', user.id, 'tournaments')
              );
              
              const tournaments = tournamentsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }));

              // Calculate participation statistics
              const participationCount = tournaments.length;
              const lastParticipation = tournaments.length > 0 
                ? tournaments.sort((a, b) => {
                    const dateA = a.date?.toDate?.() || new Date(0);
                    const dateB = b.date?.toDate?.() || new Date(0);
                    return dateB - dateA;
                  })[0].date?.toDate?.()
                : null;

              // Count by result type
              const resultStats = tournaments.reduce((acc, tournament) => {
                const result = tournament.result || 'unknown';
                acc[result] = (acc[result] || 0) + 1;
                return acc;
              }, {});

              return {
                ...user,
                participationCount,
                lastParticipation,
                tournaments,
                resultStats
              };
            } catch (error) {
              console.error(`Error loading tournaments for user ${user.id}:`, error);
              return {
                ...user,
                participationCount: 0,
                lastParticipation: null,
                tournaments: [],
                resultStats: {}
              };
            }
          })
        );

        setUsers(usersWithParticipation);
      } catch (error) {
        console.error('Error loading user participation data:', error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    loadUserParticipationData();
  }, []);

  // Sort and filter users
  const sortedAndFilteredUsers = users
    .filter(user => user.participationCount >= filterMin && user.participationCount <= filterMax)
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'participationCount':
          aValue = a.participationCount;
          bValue = b.participationCount;
          break;
        case 'pseudo':
          aValue = a.pseudo || '';
          bValue = b.pseudo || '';
          break;
        case 'lastParticipation':
          aValue = a.lastParticipation || new Date(0);
          bValue = b.lastParticipation || new Date(0);
          break;
        default:
          aValue = a.participationCount;
          bValue = b.participationCount;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const handleSort = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const getResultBadgeColor = (result) => {
    switch (result) {
      case '1st': return '#FFD700'; // Gold
      case '2nd': return '#C0C0C0'; // Silver
      case '3rd': return '#CD7F32'; // Bronze
      case 'on going': return '#4CAF50'; // Green
      case 'Participation': return '#2196F3'; // Blue
      case 'Champion': return '#FFD700'; // Yellow/Gold for Champion
      default: return '#9E9E9E'; // Gray
    }
  };

  if (loading) {
    return (
      <div className="admin-container">
        <h2>🏆 Tournament Participation Tracker</h2>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading tournament participation data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <h2>🏆 Tournament Participation Tracker</h2>
      
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/admin-panelx-952504')}>⬅️ Back to Dashboard</button>
        <button onClick={() => navigate('/admin/tournament')}>🏆 Tournament Manager</button>
      </div>

      {/* Statistics Summary */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '15px', 
        marginBottom: '20px' 
      }}>
        <div style={{ backgroundColor: '#2c3e50', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 5px 0', color: '#ecf0f1' }}>Total Users</h3>
          <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#ecf0f1' }}>{users.length}</p>
        </div>
        <div style={{ backgroundColor: '#34495e', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 5px 0', color: '#ecf0f1' }}>Active Participants</h3>
          <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#ecf0f1' }}>
            {users.filter(u => u.participationCount > 0).length}
          </p>
        </div>
        <div style={{ backgroundColor: '#2c3e50', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 5px 0', color: '#ecf0f1' }}>Total Participations</h3>
          <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#ecf0f1' }}>
            {users.reduce((sum, user) => sum + user.participationCount, 0)}
          </p>
        </div>
        <div style={{ backgroundColor: '#34495e', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 5px 0', color: '#ecf0f1' }}>Avg per User</h3>
          <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#ecf0f1' }}>
            {users.length > 0 ? (users.reduce((sum, user) => sum + user.participationCount, 0) / users.length).toFixed(1) : 0}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ 
        backgroundColor: '#f5f5f5', 
        padding: '15px', 
        borderRadius: '8px', 
        marginBottom: '20px',
        display: 'flex',
        gap: '15px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <div>
          <label style={{ marginRight: '5px' }}>Filter by participation count:</label>
          <input
            type="number"
            value={filterMin}
            onChange={(e) => setFilterMin(parseInt(e.target.value) || 0)}
            placeholder="Min"
            style={{ width: '60px', marginRight: '5px' }}
          />
          <span>to</span>
          <input
            type="number"
            value={filterMax}
            onChange={(e) => setFilterMax(parseInt(e.target.value) || 999)}
            placeholder="Max"
            style={{ width: '60px', marginLeft: '5px' }}
          />
        </div>
        <div>
          <button 
            onClick={() => { setFilterMin(0); setFilterMax(999); }}
            style={{ padding: '5px 10px', fontSize: '12px' }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Users Table */}
      {sortedAndFilteredUsers.length > 0 ? (
        <div style={{ border: '1px solid #ddd', borderRadius: '5px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0 }}>
              <tr>
                <th 
                  style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', cursor: 'pointer' }}
                  onClick={() => handleSort('pseudo')}
                >
                  User {getSortIcon('pseudo')}
                </th>
                <th 
                  style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', cursor: 'pointer' }}
                  onClick={() => handleSort('participationCount')}
                >
                  Participations {getSortIcon('participationCount')}
                </th>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Results</th>
                <th 
                  style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', cursor: 'pointer' }}
                  onClick={() => handleSort('lastParticipation')}
                >
                  Last Participation {getSortIcon('lastParticipation')}
                </th>
                <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedAndFilteredUsers.map((user) => (
                <tr key={user.id} style={{ backgroundColor: user.participationCount > 0 ? '#f9f9f9' : '#fff' }}>
                  <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                    <div>
                      <strong>{user.pseudo || 'Unknown'}</strong>
                      <div style={{ fontSize: '12px', color: '#666', fontFamily: 'monospace' }}>
                        {user.id.substring(0, 8)}...
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                    <span style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold', 
                      color: user.participationCount > 0 ? '#2e7d32' : '#666'
                    }}>
                      {user.participationCount}
                    </span>
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                      {Object.entries(user.resultStats).map(([result, count]) => (
                        <span
                          key={result}
                          style={{
                            padding: '2px 6px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            backgroundColor: getResultBadgeColor(result),
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        >
                          {result}: {count}
                        </span>
                      ))}
                      {user.participationCount === 0 && (
                        <span style={{ color: '#999', fontSize: '12px' }}>No participations</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ddd', fontSize: '12px' }}>
                    {user.lastParticipation 
                      ? user.lastParticipation.toLocaleDateString()
                      : 'Never'
                    }
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                    <button
                      onClick={() => {
                        // You can implement a detailed view here
                        alert(`User: ${user.pseudo}\nParticipations: ${user.participationCount}\nLast: ${user.lastParticipation?.toLocaleDateString() || 'Never'}`);
                      }}
                      style={{
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <h3>No users found</h3>
          <p>No users match the current filter criteria.</p>
        </div>
      )}

      {/* Footer Stats */}
      {sortedAndFilteredUsers.length > 0 && (
        <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '5px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
            <strong>Showing:</strong> {sortedAndFilteredUsers.length} users | 
            <strong> Total Participations:</strong> {sortedAndFilteredUsers.reduce((sum, user) => sum + user.participationCount, 0)} | 
            <strong> Average:</strong> {sortedAndFilteredUsers.length > 0 ? (sortedAndFilteredUsers.reduce((sum, user) => sum + user.participationCount, 0) / sortedAndFilteredUsers.length).toFixed(1) : 0}
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminTournamentParticipationTracker;
